import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { initialState } from './initialState.ts';
import {
  removeSupervisorContragentBlockAction,
  removeDeletedApplicationDocumentAction,
  setPendingAction,
  setRejectedAction,
  setFulfilledAction,
  resetCurrentContragentAction,
  resetCurrentApplicationAction,
  resetCurrentSupervisorApplicationAction,
  resetCovenantsAction,
  upsertApplicationDocumentAction,
} from '~/store/covenants/actions/actions.ts';
import {
  fetchCallCenterOperatorContragents,
  fetchMyContragents,
  fetchContragentById,
  fetchSupervisorContragentBlocks,
} from '~/store/covenants/thunks/contragentThunks.ts';
import {
  fetchRequiredDocuments,
  fetchDocumentCovenants,
  uploadDocumentSignature,
  deleteDocumentSignature,
  deleteApplicationDocument,
} from '~/store/covenants/thunks/documentThunks.ts';
import {
  fetchActiveApplications,
  fetchApplicationDocument,
  fetchApplication,
  fetchApplicationDocuments,
  fetchApplicationUnsignedDocumentsCount,
  fetchApplications,
  uploadApplicationDocument,
} from '~/store/covenants/thunks/applicationThunks.ts';
import {
  generateFinancialConditionCertificate,
  generateIncomeStructureByKvedCertificate,
  generateStateAidCertificate,
} from '~/store/covenants/thunks/certificateThunks.ts';
import {
  fetchDocumentGenerationBanks,
  fetchDocumentGenerationCurrencies,
  fetchSupervisorDocumentTemplates,
} from '~/store/covenants/thunks/dictionaryThunks.ts';
import {
  fetchSupervisorApplication,
  fetchSupervisorApplicationReadyCovenants,
  fetchSupervisorApplicationDocuments,
  fetchSupervisorApplications,
} from '~/store/covenants/thunks/supervisorApplicationThunks.ts';
import { fetchSupervisorSegments } from '~/store/covenants/thunks/supervisorSegmentThunks.ts';
import { FetchStatus } from '~/store/covenants/types.ts';
import { TApiApplicationDocument } from '~/modules/covenants/types.ts';
import type { TCovenantsAccessDeniedModal } from '~/store/covenants/types.ts';
import { login, loginManager, logout } from '~/store/users/thunks.ts';
import { clearUsersState } from '~/store/users/slices.ts';

const updateApplicationDocumentById = (
  documents: TApiApplicationDocument[],
  targetDocumentId: string,
  updater: (document: TApiApplicationDocument) => TApiApplicationDocument
) => {
  return documents.map((document) =>
    document.id === targetDocumentId ? updater(document) : document
  );
};

const removeSignatureFromApplicationDocuments = (
  documents: TApiApplicationDocument[],
  targetDocumentId: string
) =>
  updateApplicationDocumentById(documents, targetDocumentId, (document) => ({
    ...document,
    isCustomSignFile: false,
    hasSignedFile: false,
  }));

const resetSessionScopedCovenantsState = () => initialState;

const covenantsPageSlice = createSlice({
  name: 'covenantsPage',
  initialState,
  reducers: {
    resetCurrentContragent: resetCurrentContragentAction,
    resetCurrentApplication: resetCurrentApplicationAction,
    resetCurrentSupervisorApplication: resetCurrentSupervisorApplicationAction,
    resetCovenants: resetCovenantsAction,
    removeSupervisorContragentBlock: (state, action: PayloadAction<{ email: string }>) => {
      removeSupervisorContragentBlockAction(state, action.payload.email);
    },
    showAccessDeniedModal: (
      state,
      action: PayloadAction<Omit<TCovenantsAccessDeniedModal, 'open'>>
    ) => {
      state.accessDeniedModal.open = true;
      state.accessDeniedModal.message = action.payload.message;
      state.accessDeniedModal.redirectPath = action.payload.redirectPath ?? null;
    },
    hideAccessDeniedModal: (state) => {
      state.accessDeniedModal = initialState.accessDeniedModal;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(clearUsersState, resetSessionScopedCovenantsState)
      .addCase(logout.fulfilled, resetSessionScopedCovenantsState)
      .addCase(login.fulfilled, resetSessionScopedCovenantsState)
      .addCase(loginManager.fulfilled, resetSessionScopedCovenantsState);

    builder
      .addCase(fetchDocumentGenerationBanks.pending, (s) =>
        setPendingAction(s.documentGenerationBanks)
      )
      .addCase(fetchDocumentGenerationBanks.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.documentGenerationBanks, payload)
      )
      .addCase(fetchDocumentGenerationBanks.rejected, (s, { payload }) =>
        setRejectedAction(s.documentGenerationBanks, payload)
      )
      .addCase(fetchDocumentGenerationCurrencies.pending, (s) =>
        setPendingAction(s.documentGenerationCurrencies)
      )
      .addCase(fetchDocumentGenerationCurrencies.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.documentGenerationCurrencies, payload)
      )
      .addCase(fetchDocumentGenerationCurrencies.rejected, (s, { payload }) =>
        setRejectedAction(s.documentGenerationCurrencies, payload)
      )
      .addCase(fetchSupervisorDocumentTemplates.pending, (s) =>
        setPendingAction(s.supervisorDocumentTemplates)
      )
      .addCase(fetchSupervisorDocumentTemplates.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.supervisorDocumentTemplates, payload)
      )
      .addCase(fetchSupervisorDocumentTemplates.rejected, (s, { payload }) =>
        setRejectedAction(s.supervisorDocumentTemplates, payload)
      );

    builder
      .addCase(fetchMyContragents.pending, (s) => setPendingAction(s.contragents))
      .addCase(fetchMyContragents.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.contragents, payload)
      )
      .addCase(fetchMyContragents.rejected, (s, { payload }) =>
        setRejectedAction(s.contragents, payload)
      );

    builder
      .addCase(fetchCallCenterOperatorContragents.pending, (s) => setPendingAction(s.contragents))
      .addCase(fetchCallCenterOperatorContragents.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.contragents, payload)
      )
      .addCase(fetchCallCenterOperatorContragents.rejected, (s, { payload }) =>
        setRejectedAction(s.contragents, payload)
      );

    builder
      .addCase(fetchContragentById.pending, (s) => setPendingAction(s.currentContragent))
      .addCase(fetchContragentById.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.currentContragent, payload)
      )
      .addCase(fetchContragentById.rejected, (s, { payload }) =>
        setRejectedAction(s.currentContragent, payload)
      );

    builder
      .addCase(fetchSupervisorContragentBlocks.pending, (s) =>
        setPendingAction(s.supervisorContragentBlocks)
      )
      .addCase(fetchSupervisorContragentBlocks.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.supervisorContragentBlocks, payload)
      )
      .addCase(fetchSupervisorContragentBlocks.rejected, (s, { payload }) =>
        setRejectedAction(s.supervisorContragentBlocks, payload)
      );

    builder
      .addCase(fetchRequiredDocuments.pending, (s) => setPendingAction(s.requiredDocuments))
      .addCase(fetchRequiredDocuments.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.requiredDocuments, payload)
      )
      .addCase(fetchRequiredDocuments.rejected, (s, { payload }) =>
        setRejectedAction(s.requiredDocuments, payload)
      );

    builder
      .addCase(fetchDocumentCovenants.pending, (s) => setPendingAction(s.covenants))
      .addCase(fetchDocumentCovenants.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.covenants, payload)
      )
      .addCase(fetchDocumentCovenants.rejected, (s, { payload }) =>
        setRejectedAction(s.covenants, payload)
      );

    builder
      .addCase(fetchApplications.pending, (s) => setPendingAction(s.applications))
      .addCase(fetchApplications.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.applications, payload)
      )
      .addCase(fetchApplications.rejected, (s, { payload }) =>
        setRejectedAction(s.applications, payload)
      );

    builder
      .addCase(fetchActiveApplications.pending, (s) => setPendingAction(s.activeApplications))
      .addCase(fetchActiveApplications.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.activeApplications, payload)
      )
      .addCase(fetchActiveApplications.rejected, (s, { payload }) =>
        setRejectedAction(s.activeApplications, payload)
      );

    builder
      .addCase(fetchSupervisorSegments.pending, (s) => setPendingAction(s.supervisorSegments))
      .addCase(fetchSupervisorSegments.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.supervisorSegments, payload)
      )
      .addCase(fetchSupervisorSegments.rejected, (s, { payload }) =>
        setRejectedAction(s.supervisorSegments, payload)
      );

    builder
      .addCase(fetchSupervisorApplications.pending, (s) =>
        setPendingAction(s.supervisorApplications)
      )
      .addCase(fetchSupervisorApplications.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.supervisorApplications, payload)
      )
      .addCase(fetchSupervisorApplications.rejected, (s, { payload }) =>
        setRejectedAction(s.supervisorApplications, payload)
      );

    builder
      .addCase(fetchSupervisorApplicationReadyCovenants.pending, (s) =>
        setPendingAction(s.supervisorReadyCovenants)
      )
      .addCase(fetchSupervisorApplicationReadyCovenants.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.supervisorReadyCovenants, payload)
      )
      .addCase(fetchSupervisorApplicationReadyCovenants.rejected, (s, { payload }) =>
        setRejectedAction(s.supervisorReadyCovenants, payload)
      );

    builder
      .addCase(fetchSupervisorApplication.pending, (s) =>
        setPendingAction(s.currentSupervisorApplication)
      )
      .addCase(fetchSupervisorApplication.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.currentSupervisorApplication, payload)
      )
      .addCase(fetchSupervisorApplication.rejected, (s, { payload }) =>
        setRejectedAction(s.currentSupervisorApplication, payload)
      );

    builder
      .addCase(fetchSupervisorApplicationDocuments.pending, (s) =>
        setPendingAction(s.supervisorApplicationDocuments)
      )
      .addCase(fetchSupervisorApplicationDocuments.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.supervisorApplicationDocuments, payload)
      )
      .addCase(fetchSupervisorApplicationDocuments.rejected, (s, { payload }) =>
        setRejectedAction(s.supervisorApplicationDocuments, payload)
      );

    builder
      .addCase(fetchApplication.pending, (s) => setPendingAction(s.currentApplication))
      .addCase(fetchApplication.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.currentApplication, payload)
      )
      .addCase(fetchApplication.rejected, (s, { payload }) =>
        setRejectedAction(s.currentApplication, payload)
      );

    builder
      .addCase(fetchApplicationDocuments.pending, (s) => setPendingAction(s.applicationDocuments))
      .addCase(fetchApplicationDocuments.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.applicationDocuments, payload)
      )
      .addCase(fetchApplicationDocuments.rejected, (s, { payload }) =>
        setRejectedAction(s.applicationDocuments, payload)
      );

    builder
      .addCase(fetchApplicationUnsignedDocumentsCount.pending, (s) => {
        s.applicationUnsignedDocumentsCount.status = FetchStatus.Loading;
        s.applicationUnsignedDocumentsCount.error = null;
      })
      .addCase(fetchApplicationUnsignedDocumentsCount.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.applicationUnsignedDocumentsCount, payload)
      )
      .addCase(fetchApplicationUnsignedDocumentsCount.rejected, (s, { payload }) =>
        setRejectedAction(s.applicationUnsignedDocumentsCount, payload)
      );

    builder
      .addCase(generateStateAidCertificate.pending, (s) =>
        setPendingAction(s.certificateGeneration.stateAidReceived)
      )
      .addCase(generateStateAidCertificate.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.certificateGeneration.stateAidReceived, payload)
      )
      .addCase(generateStateAidCertificate.rejected, (s, { payload }) =>
        setRejectedAction(s.certificateGeneration.stateAidReceived, payload)
      )
      .addCase(generateFinancialConditionCertificate.pending, (s) =>
        setPendingAction(s.certificateGeneration.financialCondition)
      )
      .addCase(generateFinancialConditionCertificate.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.certificateGeneration.financialCondition, payload)
      )
      .addCase(generateFinancialConditionCertificate.rejected, (s, { payload }) =>
        setRejectedAction(s.certificateGeneration.financialCondition, payload)
      )
      .addCase(generateIncomeStructureByKvedCertificate.pending, (s) =>
        setPendingAction(s.certificateGeneration.incomeStructureByKved)
      )
      .addCase(generateIncomeStructureByKvedCertificate.fulfilled, (s, { payload }) =>
        setFulfilledAction(s.certificateGeneration.incomeStructureByKved, payload)
      )
      .addCase(generateIncomeStructureByKvedCertificate.rejected, (s, { payload }) =>
        setRejectedAction(s.certificateGeneration.incomeStructureByKved, payload)
      );

    builder.addCase(fetchApplicationDocument.fulfilled, (state, { payload, meta }) => {
      upsertApplicationDocumentAction(state, payload, meta.arg.baseDocument);
    });

    builder
      .addCase(uploadApplicationDocument.fulfilled, (s, { payload }) => {
        if (s.currentApplication.data) {
          s.currentApplication.data.version = payload.version;
        }
      })
      .addCase(uploadDocumentSignature.fulfilled, (s, { payload }) => {
        if (s.currentApplication.data) {
          s.currentApplication.data.version = payload.version;
        }
      })
      .addCase(deleteDocumentSignature.fulfilled, (s, { payload, meta }) => {
        if (s.currentApplication.data) {
          s.currentApplication.data.version = payload.version;
        }

        s.applicationDocuments.data.applicationDocuments = removeSignatureFromApplicationDocuments(
          s.applicationDocuments.data.applicationDocuments,
          String(meta.arg.documentId)
        );
      })
      .addCase(deleteApplicationDocument.fulfilled, (s, { payload, meta }) => {
        if (s.currentApplication.data) {
          s.currentApplication.data.version = payload.version;
        }

        removeDeletedApplicationDocumentAction(s, meta.arg.baseDocument);
      });
  },
});

export const {
  resetCurrentContragent,
  resetCurrentApplication,
  resetCurrentSupervisorApplication,
  resetCovenants,
  removeSupervisorContragentBlock,
  showAccessDeniedModal,
  hideAccessDeniedModal,
} = covenantsPageSlice.actions;

export default covenantsPageSlice.reducer;
